/**
 * Pure query-building for the command palette's record search.
 *
 * Kept dependency-free (no supabase client) for the same reason as
 * contactsQuery.ts: PostgREST filter strings are plain text that TypeScript
 * cannot validate, so the part that builds them is unit-tested in the node
 * test environment.
 */

/** Shortest query worth sending: one character matches half the table. */
export const MIN_SEARCH_LENGTH = 2;

/**
 * Turn what someone typed into a quoted `ilike` pattern that is safe to place
 * inside a PostgREST `or=(…)` filter, or `null` when there is nothing to
 * search for.
 *
 * Inside `or=(…)` the characters `, . : ( )` are grammar, so a value holding
 * any of them must be double-quoted — "St. Louis" and "Smith, John" are both
 * ordinary things to type. Quoting makes those safe; the characters that would
 * end the quoting (`"` and `\`) and the ones that are wildcards to `ilike`
 * (`%`, `_`, and PostgREST's `*` alias) are replaced with spaces so a search
 * for "100%" cannot turn into "match everything".
 */
export function toIlikePattern(raw: string): string | null {
  const cleaned = raw
    .replace(/["\\%_*]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  if (cleaned.length < MIN_SEARCH_LENGTH) return null;
  return `"%${cleaned}%"`;
}

/** `or=(a.ilike.<p>,b.ilike.<p>)` across several columns of one table. */
export function ilikeAny(columns: readonly string[], pattern: string): string {
  return columns.map((column) => `${column}.ilike.${pattern}`).join(',');
}
